import React from 'react';
import {
  Switch, Route, useLocation, Redirect,
} from 'react-router-dom';

import { DubitLocation, ModalRoutes, Routes } from './routes';

import LoginScreen from './components/login/LoginScreen';
import IslandIndexScreen from './components/island/IslandIndexScreen';
import IslandNewScreen from './components/island/IslandNewScreen';
import IslandViewScreen from './components/island/IslandViewScreen';
import RegisterScreen from './components/register/RegisterScreen';
import ForgotPasswordScreen from './components/forgot-password/ForgotPasswordScreen';
import ResetPasswordScreen from './components/reset-password/ResetPasswordScreen';
import AccountScreen from './components/account/AccountScreen';
import Template from './components/shared/Template';
import GamesViewScreen from './components/games/GamesViewScreen';
import GuestGate from './GuestGate';
import LogoutScreen from './components/logout/LogoutScreen';
import NoIslandScreen from './components/island/NoIslandsScreen';
import RegisteredUserGate from './RegisteredUserGate';

// Modals
import ManageIslandsModal from './components/island/ManageIslandsModal';
import EditIslandModal from './components/island/EditIslandModal';
import RegistrationPromptModal from './components/island/RegistrationPromptModal';

import Env from './utils/env';

const { TEACHER_PORTAL } = Env;

const Main = (): React.ReactElement => {
  const location = useLocation() as DubitLocation;

  const background = location.state?.background && {
    ...location.state.background,
    state: {
      ...location.state.background.state,
      /**
       * Add a parent flag on the background page state.
       */
      isParentOfModal: true,
    },
  };

  return !TEACHER_PORTAL ? (
    <>
      <Template>
        <Switch location={background || location}>
          <Route exact path={Routes.Islands.Games.View}>
            <GuestGate>
              <GamesViewScreen />
            </GuestGate>
          </Route>

          <Route exact path={Routes.Islands.Index}>
            <GuestGate>
              <IslandIndexScreen />
            </GuestGate>
          </Route>

          <Route exact path={Routes.Islands.New}>
            <GuestGate>
              <IslandNewScreen />
            </GuestGate>
          </Route>

          <Route path={Routes.Islands.View}>
            <GuestGate>
              <IslandViewScreen />
            </GuestGate>
          </Route>

          <Route path={Routes.Session.New}>
            <LoginScreen />
          </Route>

          <Route path={Routes.User.New}>
            <RegisterScreen />
          </Route>

          <Route
            path={Routes.PasswordReset}
            render={() => {
              /**
               * If this route has a query string reset token, we are coming here from the email link
               * to reset our password using the token.
               * Otherwise, we're requesting the forgot password email in order
               * to get a Url containinhg a reset token.
               */
              const queryParams: URLSearchParams = new URLSearchParams(location.search);
              const resetToken = queryParams.get('resetToken');

              if (resetToken) {
                return <ResetPasswordScreen resetToken={resetToken} />;
              }

              return <ForgotPasswordScreen />;
            }}
          />

          <Route path={Routes.Account.Index}>
            <RegisteredUserGate>
              <AccountScreen />
            </RegisteredUserGate>
          </Route>

          <Route exact path={Routes.Session.Destroy}>
            <LogoutScreen />
          </Route>
        </Switch>
      </Template>

      {background && (
        <Switch location={background && location}>
          <Route path={ModalRoutes.Island.ManageIslands} exact>
            <ManageIslandsModal />
          </Route>

          <Route path={ModalRoutes.Island.ManageIsland} exact>
            <ManageIslandsModal />
          </Route>

          <Route path={ModalRoutes.Island.RegistrationPrompt} exact>
            <RegistrationPromptModal />
          </Route>

          <Route path={ModalRoutes.Island.Edit} exact>
            <EditIslandModal />
          </Route>
        </Switch>
      )}
    </>
  ) : (
    <>
      <Template>
        <Switch location={background || location}>
          <Route exact path={Routes.Islands.Games.View}>
            <RegisteredUserGate>
              <GamesViewScreen />
            </RegisteredUserGate>
          </Route>

          <Route path={Routes.NoIslands}>
            <RegisteredUserGate>
              <NoIslandScreen />
            </RegisteredUserGate>
          </Route>

          <Route exact path={Routes.Islands.Index}>
            <RegisteredUserGate>
              <IslandIndexScreen />
            </RegisteredUserGate>
          </Route>

          <Route path={Routes.Islands.View}>
            <RegisteredUserGate>
              <IslandViewScreen />
            </RegisteredUserGate>
          </Route>

          <Route path={Routes.Session.New}>
            <LoginScreen />
          </Route>

          <Route path="*">
            <Redirect to={Routes.Islands.Index} />
          </Route>
        </Switch>
      </Template>

      {background && (
        <Switch location={background && location}>
          <Route path={ModalRoutes.Island.ManageIslands} exact>
            <ManageIslandsModal />
          </Route>

          <Route path={ModalRoutes.Island.ManageIsland} exact>
            <ManageIslandsModal />
          </Route>
        </Switch>
      )}
    </>
  );
};

export default Main;
